import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { CheckCircle2, XCircle, AlertCircle, History, Upload } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

interface ImportResultsProps {
  results: {
    total: number
    successful: number
    failed: number
    skipped: number
    details: Array<{
      email: string
      action: 'created' | 'skipped' | 'failed'
      error?: string
    }>
  }
  onNewImport: () => void
  onViewHistory: () => void
}

export function ImportResults({ results, onNewImport, onViewHistory }: ImportResultsProps) {
  const { total, successful, failed, skipped, details } = results

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Import Complete</CardTitle>
          <CardDescription>
            Your bulk import has finished processing. Review the results below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-center">{total}</p>
                <p className="text-sm text-muted-foreground text-center">Total</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-center text-primary">{successful}</p>
                <p className="text-sm text-muted-foreground text-center">Imported</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-center text-muted-foreground">{skipped}</p>
                <p className="text-sm text-muted-foreground text-center">Skipped</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-center text-destructive">{failed}</p>
                <p className="text-sm text-muted-foreground text-center">Failed</p>
              </CardContent>
            </Card>
          </div>

          {successful > 0 && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Successfully imported {successful} user{successful !== 1 ? 's' : ''}. 
                No emails have been sent yet. Use “Send Welcome Email” from the user list to invite them.
              </AlertDescription>
            </Alert>
          )}

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {details.map((detail, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{detail.email}</TableCell>
                    <TableCell>
                      {detail.action === 'created' && (
                        <Badge variant="outline" className="text-primary border-primary">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Imported
                        </Badge>
                      )}
                      {detail.action === 'skipped' && (
                        <Badge variant="secondary">
                          <AlertCircle className="mr-1 h-3 w-3" />
                          Skipped
                        </Badge>
                      )}
                      {detail.action === 'failed' && (
                        <Badge variant="destructive">
                          <XCircle className="mr-1 h-3 w-3" />
                          Failed
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {detail.error || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={onViewHistory}>
              <History className="mr-2 h-4 w-4" />
              View Import History
            </Button>
            <Button onClick={onNewImport}>
              <Upload className="mr-2 h-4 w-4" />
              Import More Users
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
